import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import "./AdminPanel.css";
import "./Dashboard.css";

interface CompareCategory { category: string; dim_a: number; dim_b: number; change: number; }
interface CompareAccount { account: string; category: string; dim_a: number; dim_b: number; change: number; }
interface CompareResult {
  cost_center: string; cost_center_name: string;
  dimA: string; dimB: string;
  categories: CompareCategory[]; accounts: CompareAccount[];
}

const CAT_COLOR = (cat: string): string => ({
  COMPENSATION: "#4A90E2", OCCUPANCY: "#e84393", OPERATING: "#ea580c",
  ADVERTISING: "#7c3aed", ALLOCATED: "#475569", AMORT: "#64748b",
} as Record<string, string>)[cat.toUpperCase().replace(/\s/g, "")] ?? "#94a3b8";

const EXCLUDED_CATS = new Set(["Corp OH", "CORP_OH", "Uncategorized", "OtherAdjustments", "OTHERADJUSTMENTS"]);

// Sort dimensions in actual fiscal order (FY, then Annual Budget → July →
// October → January → Planning Round 1, 2, ...) rather than alphabetically,
// which would put January before July.
const DIM_TYPE_ORDER: Record<string, number> = {
  "annual budget": 0, "july forecast": 1, "october forecast": 2, "january forecast": 3,
};
function sortDims(dims: string[]): string[] {
  const parse = (d: string) => {
    const fyMatch = d.match(/FY(\d+)/i);
    const fy = fyMatch ? parseInt(fyMatch[1], 10) : 0;
    const roundMatch = d.match(/Planning Round (\d+)/i);
    if (roundMatch) return { fy, rank: 4 + parseInt(roundMatch[1], 10) };
    const typeKey = d.toLowerCase().replace(/\s*fy\d+/i, "").trim();
    return { fy, rank: DIM_TYPE_ORDER[typeKey] ?? 99 };
  };
  return [...dims].sort((a, b) => {
    const pa = parse(a), pb = parse(b);
    return pa.fy !== pb.fy ? pa.fy - pb.fy : pa.rank - pb.rank;
  });
}

function fmtMoney(n: number): string {
  const abs = Math.abs(Number.isFinite(n) ? n : 0);
  return "$" + Math.round(abs).toLocaleString("en-US");
}
/** Accounting notation: unfavorable (positive variance) in brackets, favorable plain */
function fmtVar(n: number): string {
  const val = fmtMoney(Math.abs(n));
  return n > 0 ? `(${val})` : val;
}

interface Props {
  costCenterNumber: string;
  costCenterName?: string;
  // "cc" = this one cost center, "team" = rolled up across every CC the
  // user leads. Controlled from App.tsx, which owns the My Team pill
  // alongside the CC selector, so picking a CC always clears team mode.
  scope?: "cc" | "team";
}

export default function BudgetCompareView({ costCenterNumber, scope = "cc" }: Props) {
  const [dims, setDims] = useState<string[]>([]);
  const [dimA, setDimA] = useState("");
  const [dimB, setDimB] = useState("");
  const [result, setResult] = useState<CompareResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCat, setExpandedCat] = useState<string | null>(null);

  useEffect(() => {
    if (scope === "cc" && !costCenterNumber) return;
    setResult(null);
    const req = scope === "team"
      ? api.get("/budgets/team-compare/dimensions")
      : api.get("/budgets/compare/dimensions", { params: { cc: costCenterNumber } });
    req
      .then(res => {
        const list: string[] = sortDims(res.data.dimensions || []);
        setDims(list);
        if (list.length >= 2) {
          setDimA(list[list.length - 2]);
          setDimB(list[list.length - 1]);
        } else if (list.length === 1) {
          setDimA(list[0]);
          setDimB(list[0]);
        } else {
          setDimA(""); setDimB("");
        }
      })
      .catch(() => setDims([]));
  }, [costCenterNumber, scope]);

  const loadCompare = useCallback((a: string, b: string) => {
    if (!a || !b) return;
    setLoading(true);
    setError(null);
    const req = scope === "team"
      ? api.get("/budgets/team-compare", { params: { dimA: a, dimB: b } })
      : api.get("/budgets/compare", { params: { cc: costCenterNumber, dimA: a, dimB: b } });
    req
      .then(res => setResult(res.data))
      .catch(err => setError(err?.response?.data?.error || "Failed to load comparison"))
      .finally(() => setLoading(false));
  }, [costCenterNumber, scope]);

  useEffect(() => {
    if (dimA && dimB) loadCompare(dimA, dimB);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimA, dimB, costCenterNumber, scope]);

  const visCategories = result?.categories.filter(c => !EXCLUDED_CATS.has(c.category)) ?? [];
  const totalA = visCategories.reduce((s, c) => s + c.dim_a, 0);
  const totalB = visCategories.reduce((s, c) => s + c.dim_b, 0);
  const totalChg = totalB - totalA;
  const totalPct = totalA > 0 ? (totalChg / totalA) * 100 : 0;

  return (
    <div className="admin-section" style={{ margin: "16px 0" }}>
      <div className="admin-section-header">
        <span className="material-icons">compare_arrows</span>
        <span className="admin-section-title">Forecast Comparison</span>
      </div>
      <div className="admin-section-body">
        <div className="fc-toolbar">
          <div className="fc-toolbar-selectors">
            <div className="fc-dim-group">
              <span className="fc-dim-group-label">Base</span>
              <select className="fc-dim-select" value={dimA} onChange={e => setDimA(e.target.value)}>
                {dims.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <span className="fc-vs-badge">vs</span>
            <div className="fc-dim-group">
              <span className="fc-dim-group-label">Compare</span>
              <select className="fc-dim-select" value={dimB} onChange={e => setDimB(e.target.value)}>
                {dims.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div className="fc-toolbar-actions">
            <button className="fc-refresh-btn" onClick={() => loadCompare(dimA, dimB)}>
              <span className="material-icons">refresh</span>
            </button>
          </div>
        </div>

        {result?.cost_center_name && (
          <div style={{ fontSize: 12, fontWeight: 600, color: "oklch(0.5 0.02 250)", margin: "-10px 0 14px" }}>
            {scope === "team" ? "Rolled up across " : "Scoped to "}{result.cost_center_name}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 24, color: "#9ca3af", fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, color: "oklch(0.5 0.18 25)", fontSize: 13 }}>{error}</div>
        ) : !result ? (
          <div style={{ padding: 24, color: "#9ca3af", fontSize: 13 }}>
            {dims.length < 2
              ? "Not enough budget history yet to compare — this cost center needs data in at least two dimensions (e.g. a prior round and the current one)."
              : "Pick two dimensions to compare."}
          </div>
        ) : (
          <>
            <div className="fc-summary-strip">
              <div className="fc-summary-cell">
                <span className="fc-summary-label">Base</span>
                <span className="fc-summary-value">{fmtMoney(totalA)}</span>
              </div>
              <span className="fc-summary-arrow material-icons">arrow_forward</span>
              <div className="fc-summary-cell">
                <span className="fc-summary-label">Compare</span>
                <span className="fc-summary-value">{fmtMoney(totalB)}</span>
              </div>
              <div className="fc-summary-divider" />
              <div className={`fc-summary-cell ${totalChg > 0 ? "fc-summary-cell--unfav" : totalChg < 0 ? "fc-summary-cell--fav" : ""}`}>
                <span className="fc-summary-label">Net Change</span>
                <span className="fc-summary-value">{fmtVar(totalChg)}</span>
              </div>
              <div className={`fc-summary-cell ${totalChg > 0 ? "fc-summary-cell--unfav" : totalChg < 0 ? "fc-summary-cell--fav" : ""}`}>
                <span className="fc-summary-label">Change %</span>
                <span className="fc-summary-value">{totalA > 0 ? `${totalPct > 0 ? "+" : ""}${totalPct.toFixed(1)}%` : "—"}</span>
              </div>
            </div>

            <div style={{ overflowX: "auto", marginTop: 16 }}>
              <table className="fv-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="fv-th-right">{dimA}</th>
                    <th className="fv-th-right">{dimB}</th>
                    <th className="fv-th-right">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {visCategories.map(c => (
                    <React.Fragment key={c.category}>
                      <tr style={{ cursor: "pointer" }} onClick={() => setExpandedCat(expandedCat === c.category ? null : c.category)}>
                        <td style={{ fontWeight: 700 }}>
                          <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", transform: expandedCat === c.category ? "rotate(90deg)" : "none", transition: "transform .15s" }}>chevron_right</span>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: CAT_COLOR(c.category), margin: "0 6px" }} />
                          {c.category}
                        </td>
                        <td className="fv-amount">{fmtMoney(c.dim_a)}</td>
                        <td className="fv-amount">{fmtMoney(c.dim_b)}</td>
                        <td className={`fv-amount ${c.change > 0 ? "dash-unfav" : c.change < 0 ? "dash-fav" : ""}`}>{fmtVar(c.change)}</td>
                      </tr>
                      {expandedCat === c.category && result.accounts.filter(a => a.category === c.category).map(a => (
                        <tr key={a.account} style={{ background: "oklch(0.98 0.003 250)" }}>
                          <td style={{ paddingLeft: 36, fontSize: 12, color: "oklch(0.4 0.02 250)" }}>{a.account}</td>
                          <td className="fv-amount" style={{ fontSize: 12 }}>{fmtMoney(a.dim_a)}</td>
                          <td className="fv-amount" style={{ fontSize: 12 }}>{fmtMoney(a.dim_b)}</td>
                          <td className={`fv-amount ${a.change > 0 ? "dash-unfav" : a.change < 0 ? "dash-fav" : ""}`} style={{ fontSize: 12 }}>{fmtVar(a.change)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, borderTop: "2px solid oklch(0.9 0.01 250)" }}>
                    <td>Total</td>
                    <td className="fv-amount">{fmtMoney(totalA)}</td>
                    <td className="fv-amount">{fmtMoney(totalB)}</td>
                    <td className={`fv-amount ${totalChg > 0 ? "dash-unfav" : totalChg < 0 ? "dash-fav" : ""}`}>{fmtVar(totalChg)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
